import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { extractRelationshipVariables, RelationshipVariables } from 'support/helpers/relationships/relationships';
import { RelationshipV2 } from 'support/types';

export const buildTaskVariables = (
  extracted: RelationshipVariables | undefined,
  t: (key: string) => string,
): Record<string, string> => {
  if (!extracted) {
    return {};
  }
  const prepositionKey = extracted.direction === 'OUTBOUND' ? 'to' : 'from';
  return {
    partnerName: extracted.partnerName,
    messageType: t(`common:messageTypes.plural.${extracted.messageType}`),
    direction: t(`connections:direction.present.${extracted.direction}`),
    directionPast: t(`connections:direction.past.${extracted.direction}`),
    toFromPreposition: t(`connections:to_from_preposition.${prepositionKey}`),
  };
};

export const useTaskVariables = (
  relationship: RelationshipV2 | undefined,
  responsiblePartnerId?: string,
): Record<string, string> => {
  const { t } = useTranslation();
  const { data: currentPartner } = useCurrentPartner();

  return useMemo(() => {
    const effectivePartnerId = responsiblePartnerId ?? currentPartner?.id;
    if (!relationship || !effectivePartnerId) {
      return {};
    }
    const extracted = extractRelationshipVariables(relationship, effectivePartnerId);
    return buildTaskVariables(extracted, t);
  }, [relationship, responsiblePartnerId, currentPartner?.id, t]);
};

/**
 * i18n
 *
 * t('connections:direction.present.INBOUND')
 * t('connections:direction.present.OUTBOUND')
 * t('connections:direction.past.INBOUND')
 * t('connections:direction.past.OUTBOUND')
 * t('connections:to_from_preposition.to')
 * t('connections:to_from_preposition.from')
 *
 */
